import { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import axios from '../../api/axios';
import AuthContext from '../../api/AuthProvider';
import SidebarLeft from '../components/SidebarLeft/SidebarLeft';
import SidebarRight from '../components/SidebarRight/SidebarRight';
import Header from '../components/Header';
import ProfilePic from '../components/Profile/ProfilePicture';
import PersonalInfo from '../components/Profile/PersonalInfo';
import AboutMe from '../components/Profile/AboutMe';
import InvestorInfo from '../components/Profile/InvestorInfo';
import FollowButton from '../components/Profile/FollowButton';
import AccountDetails from '../components/Profile/AccountDetails';

const emptyProfile = {
    user: {},
    description: '',
    image_src: '',
    city: '',
    state: '',
    country: '',
    strategies: {},
    classes: {},
};

const Profile = () => {
    const navigate = useNavigate();
    const { username } = useParams();
    const { user, updateUser, replaceAuthTokens } = useContext(AuthContext);
    const [profile, setProfile] = useState(emptyProfile);
    const [isLoading, setIsLoading] = useState(true);
    const [errMsg, setErrMsg] = useState('');
    const [followers, setFollowers] = useState([]);
    const [usersFollowed, setUsersFollowed] = useState([]);
    const [isCurrentUserFollowed, setIsCurrentUserFollowed] = useState(false);

    const profileUser = profile.user || {};
    const isLoggedInUser = username === user?.username;
    const fullName = [profileUser.first_name, profileUser.last_name]
        .filter(Boolean)
        .join(' ');
    const location = [profile.city, profile.state].filter(Boolean).join(', ');

    useEffect(() => {
        async function fetchUserProfile() {
            setIsLoading(true);
            setErrMsg('');
            try {
                const response = await axios.get(`/api/profile/${username}`);
                setProfile(response.data);
            } catch (error) {
                console.error(error);
                setErrMsg('Unable to load this profile.');
            } finally {
                setIsLoading(false);
            }
        }

        async function getFollowers() {
            try {
                const response = await axios.get(
                    `/api/profile/${user.user_id}/followers`
                );
                setFollowers(response.data);
            } catch (error) {
                console.error(error);
            }
        }

        async function getUsersFollowed() {
            try {
                const response = await axios.get(
                    `/api/profile/${user.user_id}/users-followed`
                );
                setUsersFollowed(response.data);
                setIsCurrentUserFollowed(
                    Boolean(
                        response.data.find(
                            (followObject) =>
                                followObject.user.username === username
                        )
                    )
                );
            } catch (error) {
                console.error(error);
            }
        }

        fetchUserProfile();
        getFollowers();
        getUsersFollowed();
    }, [username, user.user_id]);

    const applyProfileResponse = (nextProfile) => {
        if (nextProfile.auth_tokens) {
            replaceAuthTokens(nextProfile.auth_tokens);
        }
        setProfile(nextProfile);
        if (nextProfile.user?.id === user.user_id) {
            updateUser({
                username: nextProfile.user.username,
                firstname: nextProfile.user.first_name,
                lastname: nextProfile.user.last_name,
            });
        }
    };

    const handleProfileEdit = async (fields) => {
        const response = await axios.post(
            `/api/profile/${user.user_id}/edit`,
            JSON.stringify(fields),
            {
                headers: { 'Content-Type': 'application/json' },
                withCredentials: true,
            }
        );
        applyProfileResponse(response.data);
        if (fields.username && fields.username !== username) {
            navigate(`/profile/${response.data.user.username}`);
        }
        return response.data;
    };

    const handleProfilePhotoUpload = async (imageFile) => {
        const formData = new FormData();
        formData.append('image', imageFile);
        const response = await axios.post(
            `/api/profile/${user.user_id}/photo`,
            formData,
            { withCredentials: true }
        );
        applyProfileResponse(response.data);
        return response.data;
    };

    const handleUserFollow = async () => {
        await axios.post(
            `/api/profile/${user.user_id}/follow`,
            JSON.stringify({
                followed_username: username,
            }),
            {
                headers: { 'Content-Type': 'application/json' },
                withCredentials: true,
            }
        );
        setIsCurrentUserFollowed(!isCurrentUserFollowed);
    };

    return (
        <div className="page-profile">
            <div className="subpage-profile">
                <div className="section-sidebar">
                    <SidebarLeft
                        followers={followers}
                        usersFollowed={usersFollowed}
                    />
                </div>
                <main className="section-center">
                    <div className="header-profile">
                        <Header />
                    </div>
                    <div className="profile-shell">
                        {isLoading || errMsg ? (
                            <div
                                className={`profile-status ${
                                    errMsg ? 'profile-status-error' : ''
                                }`}
                            >
                                {errMsg || 'Loading profile...'}
                            </div>
                        ) : (
                            <>
                                <section className="profile-hero">
                                    <ProfilePic
                                        imageSrc={profile.image_src}
                                        isLoggedInUser={isLoggedInUser}
                                        onUpload={handleProfilePhotoUpload}
                                    />
                                    <div className="profile-hero-content">
                                        <p className="profile-eyebrow">
                                            @{profileUser.username}
                                        </p>
                                        <h1>{fullName || 'Finspace User'}</h1>
                                        <p className="profile-hero-meta">
                                            {location || 'Location not added'}
                                        </p>
                                        {!isLoggedInUser && (
                                            <FollowButton
                                                isFollowed={
                                                    isCurrentUserFollowed
                                                }
                                                onClickCallback={
                                                    handleUserFollow
                                                }
                                            />
                                        )}
                                    </div>
                                </section>
                                <div className="profile-grid">
                                    <div className="profile-grid-left">
                                        <AboutMe
                                            description={profile.description}
                                            handleProfileEdit={
                                                handleProfileEdit
                                            }
                                            isLoggedInUser={isLoggedInUser}
                                        />
                                    </div>
                                    <div className="profile-grid-right">
                                        <PersonalInfo
                                            birthday={profileUser.birthday}
                                            handleProfileEdit={
                                                handleProfileEdit
                                            }
                                            isLoggedInUser={isLoggedInUser}
                                            city={profile.city}
                                            state={profile.state}
                                            country={profile.country}
                                        />
                                        <InvestorInfo
                                            classes={profile.classes}
                                            strategies={profile.strategies}
                                            handleProfileEdit={
                                                handleProfileEdit
                                            }
                                            isLoggedInUser={isLoggedInUser}
                                        />
                                        {isLoggedInUser && (
                                            <AccountDetails
                                                userData={profileUser}
                                                handleProfileEdit={
                                                    handleProfileEdit
                                                }
                                            />
                                        )}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </main>
                <div className="section-sidebar">
                    <SidebarRight />
                </div>
            </div>
        </div>
    );
};

export default Profile;
